import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useAnimationFrame } from 'framer-motion';
import { COLORS } from '../constants/colors.ts';
import { YOUR_GOOGLE_APPS_SCRIPT_URL } from '../constants/event.ts';
import { fadeIn } from '../constants/animations.ts';

interface Wish {
  name: string;
  wishes: string;
  timestamp: string;
}

interface WishesSectionProps {
  refreshKey?: number;
}

const POLL_INTERVAL_MS = 30_000;
const CARD_WIDTH = 270;
const CARD_GAP = 16;
const SCROLL_SPEED = 38; // px per second
const TEXT_MAX_HEIGHT = 88; // ~4 lines at 1.5 line-height × 14px font-size

const getInitials = (name: string): string =>
  name.trim().split(/\s+/).slice(0, 2).map(n => n.charAt(0).toUpperCase()).join('');

// ─── Modal ───────────────────────────────────────────────────────────────────

const WishModal: React.FC<{ wish: Wish | null; onClose: () => void }> = ({ wish, onClose }) => (
  <AnimatePresence>
    {wish && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 flex items-center justify-center px-6"
        style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.93, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.93, y: 18 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          className="relative w-full max-w-sm"
          style={{
            background: COLORS.primary,
            border: `1px solid ${COLORS.secondary}25`,
            borderRadius: '16px',
            padding: '24px 22px 22px',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-3.5 right-4 font-optima text-lg leading-none"
            style={{ color: `${COLORS.secondary}55` }}
            aria-label="Close"
          >
            ✕
          </button>

          {/* Avatar + name */}
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold font-optima"
              style={{
                background: `${COLORS.accent}20`,
                border: `1px solid ${COLORS.accent}40`,
                color: COLORS.accent,
              }}
            >
              {getInitials(wish.name)}
            </div>
            <p className="font-optima text-sm font-semibold" style={{ color: COLORS.secondary }}>
              {wish.name}
            </p>
          </div>

          {/* Full wish — preserve formatting */}
          <p
            className="font-optima text-sm leading-relaxed"
            style={{ color: `${COLORS.secondary}BB`, whiteSpace: 'pre-wrap' }}
          >
            {wish.wishes}
          </p>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Card ─────────────────────────────────────────────────────────────────────

const WishCard: React.FC<{ wish: Wish; isNew: boolean; onClick: () => void }> = ({
  wish,
  isNew,
  onClick,
}) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight + 2);
    }
  }, [wish.wishes]);

  return (
    <motion.div
      className="relative flex-shrink-0 flex flex-col cursor-pointer"
      style={{
        width: `${CARD_WIDTH}px`,
        background: `${COLORS.secondary}0A`,
        border: `1px solid ${COLORS.secondary}1A`,
        borderRadius: '14px',
        padding: '18px 18px 16px',
      }}
      animate={
        isNew
          ? {
              boxShadow: [
                `0 0 0px 0px ${COLORS.accent}00`,
                `0 0 14px 4px ${COLORS.accent}38`,
                `0 0 8px 2px ${COLORS.accent}20`,
                `0 0 14px 4px ${COLORS.accent}38`,
                `0 0 0px 0px ${COLORS.accent}00`,
              ],
              borderColor: [
                `${COLORS.secondary}1A`,
                `${COLORS.accent}60`,
                `${COLORS.accent}35`,
                `${COLORS.accent}60`,
                `${COLORS.secondary}1A`,
              ],
            }
          : {}
      }
      transition={isNew ? { duration: 2.4, repeat: 2, ease: 'easeInOut' } : {}}
      onClick={onClick}
    >
      {/* NEW badge */}
      <AnimatePresence>
        {isNew && (
          <motion.span
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.7 }}
            transition={{ duration: 0.25 }}
            className="absolute top-3 right-3 text-[9px] font-optima font-bold tracking-widest uppercase px-2 py-0.5 rounded-full"
            style={{
              backgroundColor: `${COLORS.accent}20`,
              color: COLORS.accent,
              border: `1px solid ${COLORS.accent}44`,
            }}
          >
            New
          </motion.span>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold font-optima"
          style={{
            background: `${COLORS.accent}20`,
            border: `1px solid ${COLORS.accent}40`,
            color: COLORS.accent,
            letterSpacing: '0.05em',
          }}
        >
          {getInitials(wish.name)}
        </div>
        <p
          className="font-optima text-sm font-semibold leading-tight"
          style={{ color: COLORS.secondary }}
        >
          {wish.name}
        </p>
      </div>

      {/* Truncated wish text */}
      <div className="relative">
        <p
          ref={textRef}
          className="font-optima text-sm leading-relaxed overflow-hidden"
          style={{
            color: `${COLORS.secondary}99`,
            maxHeight: `${TEXT_MAX_HEIGHT}px`,
            whiteSpace: 'pre-wrap',
          }}
        >
          {wish.wishes}
        </p>

        {/* Gradient fade + read more hint when truncated */}
        {isTruncated && (
          <div
            className="absolute bottom-0 left-0 right-0 flex items-end justify-end pt-6"
            style={{
              background: `linear-gradient(to bottom, transparent, ${COLORS.primary}E0 80%)`,
            }}
          >
            <span
              className="font-optima text-[10px] tracking-wider"
              style={{ color: `${COLORS.accent}99` }}
            >
              tap to read more
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ─── Scrollable track ─────────────────────────────────────────────────────────

const WishTrack: React.FC<{
  wishes: Wish[];
  newWishKey: string | null;
  onCardClick: (wish: Wish) => void;
}> = ({ wishes, newWishKey, onCardClick }) => {
  const isDraggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const x = useMotionValue(0);

  const totalTrackWidth = wishes.length * (CARD_WIDTH + CARD_GAP) + CARD_GAP;
  const maxScrollLeft = Math.max(0, totalTrackWidth - containerWidth);
  const canScroll = maxScrollLeft > 0;

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) setContainerWidth(containerRef.current.offsetWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useAnimationFrame((_, delta) => {
    if (isDraggingRef.current || !canScroll) return;
    let next = x.get() - (delta / 1000) * SCROLL_SPEED;
    if (next < -maxScrollLeft) next = 0;
    x.set(next);
  });

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    // Clamp to valid range after drag
    const curr = x.get();
    if (curr > 0) x.set(0);
    if (curr < -maxScrollLeft) x.set(-maxScrollLeft);
  };

  return (
    <div ref={containerRef} className="overflow-hidden">
      <motion.div
        drag={canScroll ? 'x' : false}
        dragConstraints={{ left: -maxScrollLeft, right: 0 }}
        dragElastic={0}
        dragMomentum={false}
        onDragStart={() => { isDraggingRef.current = true; }}
        onDragEnd={handleDragEnd}
        className={`flex pb-4 ${canScroll ? 'cursor-grab active:cursor-grabbing' : ''}`}
        style={{ x, width: 'max-content', gap: `${CARD_GAP}px`, paddingLeft: `${CARD_GAP}px` }}
      >
        {wishes.map((wish, i) => {
          const cardKey = `${wish.name}-${wish.timestamp}`;
          return (
            <WishCard
              key={`${cardKey}-${i}`}
              wish={wish}
              isNew={cardKey === newWishKey}
              onClick={() => onCardClick(wish)}
            />
          );
        })}
      </motion.div>
    </div>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonCard: React.FC<{ index: number }> = ({ index }) => (
  <div
    className="flex-shrink-0 w-[270px] animate-pulse"
    style={{
      background: `${COLORS.secondary}08`,
      border: `1px solid ${COLORS.secondary}15`,
      borderRadius: '14px',
      padding: '18px 18px 16px',
      animationDelay: `${index * 0.15}s`,
    }}
  >
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-full" style={{ background: `${COLORS.secondary}15` }} />
      <div className="h-3 rounded w-28" style={{ background: `${COLORS.secondary}12` }} />
    </div>
    <div className="space-y-2">
      <div className="h-2.5 rounded" style={{ background: `${COLORS.secondary}0E`, width: '92%' }} />
      <div className="h-2.5 rounded" style={{ background: `${COLORS.secondary}0E`, width: '78%' }} />
      <div className="h-2.5 rounded" style={{ background: `${COLORS.secondary}0E`, width: '55%' }} />
    </div>
  </div>
);

// ─── Section ──────────────────────────────────────────────────────────────────

const WishesSection: React.FC<WishesSectionProps> = ({ refreshKey = 0 }) => {
  const [wishes, setWishes] = useState<Wish[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newWishKey, setNewWishKey] = useState<string | null>(null);
  const [activeWish, setActiveWish] = useState<Wish | null>(null);
  const latestTopKeyRef = useRef<string | null>(null);

  const fetchWishes = useCallback(async (markNew = false) => {
    try {
      const res = await fetch(YOUR_GOOGLE_APPS_SCRIPT_URL, { method: 'GET' });
      const json = await res.json();

      if (json.status === 'success') {
        const filtered = (json.wishes as Wish[]).filter(r => r.wishes?.trim());
        setWishes(filtered);
        setError(null);

        if (markNew && filtered.length > 0) {
          const topKey = `${filtered[0].name}-${filtered[0].timestamp}`;
          if (topKey !== latestTopKeyRef.current) {
            latestTopKeyRef.current = topKey;
            setNewWishKey(topKey);
            setTimeout(() => setNewWishKey(null), 8000);
          }
        } else if (filtered.length > 0 && latestTopKeyRef.current === null) {
          latestTopKeyRef.current = `${filtered[0].name}-${filtered[0].timestamp}`;
        }
      } else {
        setError('Could not load wishes.');
      }
    } catch {
      setError('Could not load wishes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWishes();
    const interval = setInterval(() => fetchWishes(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchWishes]);

  useEffect(() => {
    if (refreshKey <= 0) return;
    const timer = setTimeout(() => fetchWishes(true), 2000);
    return () => clearTimeout(timer);
  }, [refreshKey, fetchWishes]);

  const wishCount = wishes.length;

  return (
    <>
      <WishModal wish={activeWish} onClose={() => setActiveWish(null)} />

      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeIn}
        className="py-14 overflow-hidden"
        style={{ backgroundColor: COLORS.primary }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-8 px-6 space-y-2"
        >
          <span className="font-glamour text-3xl font-bold" style={{ color: COLORS.secondary }}>
            Wedding Wishes
          </span>
          <p className="font-optima text-sm" style={{ color: `${COLORS.secondary}80` }}>
            Messages from our loved ones
          </p>
          {!loading && wishCount > 0 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="font-optima text-xs tracking-widest uppercase"
              style={{ color: `${COLORS.accent}BB` }}
            >
              {wishCount} {wishCount === 1 ? 'wish' : 'wishes'} received
            </motion.p>
          )}
        </motion.div>

        {loading && (
          <div className="flex gap-4 overflow-hidden px-4">
            {[0, 1, 2].map(i => <SkeletonCard key={i} index={i} />)}
          </div>
        )}

        {!loading && error && (
          <p className="text-center font-optima text-sm px-6" style={{ color: `${COLORS.secondary}55` }}>
            {error}
          </p>
        )}

        {!loading && !error && wishCount === 0 && (
          <p className="text-center font-optima text-sm italic px-6" style={{ color: `${COLORS.secondary}44` }}>
            Be the first to leave a wish above.
          </p>
        )}

        {!loading && !error && wishCount > 0 && (
          <div className="relative">
            <div
              className="absolute left-0 top-0 bottom-4 w-8 z-10 pointer-events-none"
              style={{ background: `linear-gradient(to right, ${COLORS.primary}, transparent)` }}
            />
            <div
              className="absolute right-0 top-0 bottom-4 w-8 z-10 pointer-events-none"
              style={{ background: `linear-gradient(to left, ${COLORS.primary}, transparent)` }}
            />
            <WishTrack
              wishes={wishes}
              newWishKey={newWishKey}
              onCardClick={setActiveWish}
            />
          </div>
        )}
      </motion.section>
    </>
  );
};

export default WishesSection;
